/**
 * Arranging a score as a job: upload it, start the run, and ask how far it has got.
 *
 * A whole movement of a symphony takes tens of seconds to arrange, longer than a request should
 * be held open, so the arrange route starts the run and answers with a job id at once. The
 * caller polls the job for its progress and, when it is done, the arrangement. A score from
 * the library is named by its id and read by the server from disk; any other score is
 * uploaded first, as raw bytes, and named by the upload id the server hands back.
 */

import type { AiLevel, ArrangeHint, ArrangeLayer, ArrangeMore, ArrangeStart } from "./ai";
import type { JobStarted, TheoryJob } from "./job";
import type { ArrangePayload } from "./tune";
import type { ArrangeWishes } from "./wishes";

/** The most bytes an uploaded score may run to. The server refuses anything larger with 413. */
export const UPLOAD_LIMIT = 50 * 1024 * 1024;

/** What `POST /theory/uploads` answers: the id to name the upload by when arranging it. */
export interface UploadPayload {
  upload: string;
}

/**
 * How far the harmony may go beyond the source's notes. `written` plays only them. `fill` gives
 * players with nothing to play the notes of each chord that nobody sounds -- the chord the
 * source's chord symbols name, else the rule of the octave's -- and under a tune sounding
 * alone, the chord a symbol names, with its bass. `complete` does the same, and where no symbol
 * is in force chooses the chord a tune sounding alone is given, from the key's own chords.
 * `free` keeps only the tune, leaves the rest of the source out, and has the players left play
 * chords chosen for the tune in its place, secondary dominants among them.
 */
export const HARMONY_LEVELS = ["written", "fill", "complete", "free"] as const;
export type HarmonyLevel = (typeof HARMONY_LEVELS)[number];

/**
 * How the notes a harmony level adds are played; the source's own notes are never touched.
 * `held` holds each for as long as its chord lasts. `repeated` strikes each again on every beat
 * (a dotted beat in a compound metre) counted from the bar line. `broken` has one player walk
 * each chord -- two notes to a beat, three in a compound metre -- above its lowest note and
 * below the tune, while the others hold. `alberti` has that player play three neighbouring chord
 * notes within an octave -- lowest, highest, middle, highest -- or two where only two fit; another
 * take may choose another figure. `waltz` plays the lowest added note on the bar's first beat and
 * the rest on the beats after; `march` the lowest on every other beat from the first and the rest
 * between; `syncopated` strikes the rest just after each beat, held across the next, over the
 * lowest held.
 */
export const TEXTURES = [
  "held",
  "repeated",
  "broken",
  "alberti",
  "waltz",
  "march",
  "syncopated",
] as const;
export type Texture = (typeof TEXTURES)[number];

/**
 * A counter-melody of the arranger's own against the tune, for the highest player the source
 * gives nothing to; none where every player has notes. `plain` plays a chord note on each beat,
 * moving against the tune where it can, below it and above the bass, never in parallel fifths or
 * octaves with another part. `flowing` adds passing and neighbour notes between the beats. `off`
 * writes none. Another take may choose another line.
 */
export const COUNTERS = ["off", "plain", "flowing"] as const;
export type Counter = (typeof COUNTERS)[number];

/**
 * The tune varied with notes of the arranger's own between its notes, each at the end of a
 * tune note that moves straight on to the next, which is cut short to make room. `light` fills
 * its thirds and fourths with passing notes; `more` also turns a repeated note into a neighbour
 * note and puts a turn before a step from a note two beats long or more; `free` also runs
 * across leaps up to an octave. Only where the ornament stays above every other part and moves
 * in no parallel fifths or octaves with one. `off` plays the tune as written. Another take may
 * choose another neighbour note or turn.
 */
export const VARIATIONS = ["off", "light", "more", "free"] as const;
export type Variation = (typeof VARIATIONS)[number];

export interface ArrangeRequest extends ArrangeWishes {
  /** An id `POST /theory/uploads` handed back. Give this or `score`, not both. */
  upload?: string;
  /** A library score's id. Give this or `upload`, not both. */
  score?: string;
  roster: string[];
  /** An inclusive pair of bar numbers, counted from 1, or absent for the whole score. */
  bars?: [number, number];
  title?: string;
  name?: string;
  /** How far the harmony may go beyond the source's notes. `written` when absent. */
  harmony?: HarmonyLevel;
  /** How the notes the harmony adds are played. `held` when absent. */
  texture?: Texture;
  /** Take the tune from the next staff down while the top staff rests. Off when absent. */
  follow?: boolean;
  /** Take the bass from the next staff up while the lowest staff rests. Off when absent. */
  rise?: boolean;
  /** Seat a moving line's short notes before a held note. Off when absent. */
  moving?: boolean;
  /** Play an inner note no player is free for as a string player's double stop. Off when absent. */
  stops?: boolean;
  /**
   * Have players given nothing double the tune, the bass and the inner lines, where the source
   * marks them loud enough. Off when absent.
   */
  double?: boolean;
  /**
   * Have players given nothing share the tune, the bass and the inner lines by turns, a phrase
   * at a time, the inner notes gathered on as few players as can play them. It says who plays a
   * note and `double` how many do. Not with a plan of who rests (`ai`). Off when absent.
   */
  share?: boolean;
  /** A counter-melody for a player given nothing. `off` when absent. */
  counter?: Counter;
  /** How far the tune is varied. `off` when absent. */
  vary?: Variation;
  /**
   * Which take, from 1: the first makes every near-equal choice the ranking puts first, so the
   * same request always gives the same arrangement; 2, 3 and on choose among near-equal chords
   * and voicings instead, each the same every time it is asked for. 1 when absent.
   */
  take?: number;
  /** How far the AI arranger takes part. `off` when absent. */
  ai?: AiLevel;
  /**
   * Where the arranging starts: from the score's own notes, or anew from its tune and chords
   * at the `ai` level. From the tune every other choice above is left at its own value, `check`
   * is off, and a piano, a harp, an organ or a celesta is refused. From the note model (`model`) only `take` is taken, as
   * the model's seed; the check is always made, `ai` is left `off`, and a roster of one
   * player who sounds a note at a time is refused. Where the model cannot write -- it is not
   * installed, or a bar is longer than it reads -- the answer is the one `tune` gives at `ai`
   * `normal`, and its `unwritten` says why. `score` when absent.
   */
  start?: ArrangeStart;
  /**
   * The note model's add-ons put on: the fitted ones, each telling it one thing more of the
   * source than its tune, and the held (`ARRANGE_HELD`), each a limit on what it may write.
   * Taken only where `start` is `model`, and refused anywhere else unless empty. A fitted
   * add-on that is not installed is the model not able to write: the answer is `tune`'s, and
   * its `unwritten` says why. None when absent.
   */
  hints?: readonly ArrangeHint[];
  /**
   * How far the fitted add-ons are put on, from 0 to 1: 1 as they were fitted, 0 the model without
   * them, and between them the model follows the score that much less and keeps that much more
   * to what it would write by itself. Taken only where `start` is `model`. 1 when absent.
   */
  strength?: number;
  /**
   * The layers the `layers` add-on is told, the others left for the model to decide. Taken only
   * where `hints` has `layers`, and refused anywhere else. All of them when absent.
   */
  layers?: readonly ArrangeLayer[];
  /**
   * How much more than the score the `more` add-on is asked for (`ARRANGE_MORE`). Taken only
   * where `hints` has `more`, and refused anywhere else. `MORE_USUAL` when absent.
   */
  more?: ArrangeMore;
  /**
   * Change the arranger's own notes where the book's rules flag them, and only where that leaves
   * the checker fewer faults; never a note of the source's. A search, not the AI arranger. Off
   * when absent.
   */
  check?: boolean;
}

/** What `POST /theory/arrange` answers: the job to poll. */
export type ArrangeStarted = JobStarted;

/**
 * How far a run has got, as the pipeline last reported it.
 *
 * `queued` until the pipeline has said anything; `cut` once the bars asked for have been cut
 * out of the score; `planning` while the AI arranger plans, where it was asked to; `reading`
 * while the score is read and its notes handed to the players, or, starting from the tune, while
 * its tune is read and arranged anew; `writing` while the note model writes the parts under
 * the tune, where arranging starts from it.
 */
export type ArrangeStage = "queued" | "cut" | "planning" | "reading" | "writing";

export interface ArrangeProgress {
  stage: ArrangeStage;
  /** The bars being arranged, once the cut has settled them. */
  first?: number;
  last?: number;
}

/** What `GET /theory/arrange/:job` answers: the arrangement is its result. */
export type ArrangeJob = TheoryJob<ArrangeProgress, ArrangePayload>;
